<script module>
	import { tick } from "svelte";

	export function useValidations() {
		let errors = $state({});

		function validateForm(schema, data) {
			resetErrors();
			return schema
				.validate(data, { abortEarly: false })
				.catch((validationError) => {
					validationError.inner.forEach((error) => {
						setError(error.path, error.errors);
					});
				});
		}

		function validateField(schema, fieldString, data) {
			removeError(fieldString);
			return schema
				.validateAt(fieldString, data, { abortEarly: false })
				.catch((validationError) => {
					validationError.inner.forEach((error) => {
						setError(error.path, error.errors);
					});
				});
		}

		function setError(field, errorMessages) {
			errors[field] = errorMessages;
		}

		function removeError(field) {
			delete errors[field];
		}

		function resetErrors() {
			errors = {};
		}

		function getErrors() {
			return errors;
		}

		return {
			removeError,
			resetErrors,
			setError,
			validateField,
			validateForm,
			getErrors,
		};
	}

	/**
	 * A reusable Svelte action for validating form inputs.
	 * It listens for `input` events and sets a custom validity message.
	 * Usage: add use:validity={validationMethod} on the input tag
	 * @param {HTMLInputElement} input The input element to validate.
	 * @param {(value: string) => string | null | undefined} validator A function that returns an error message or an empty string.
	 */
	export function validity(input, validator) {
		function validate() {
			// Call the validator function and set the message.
			// An empty string means the element is valid.
			const message = validator(input.value) ?? "";
			input.setCustomValidity(message);
		}

		// Run validation on initial mount and whenever the input changes.
		validate();
		input.addEventListener("input", validate);

		return {
			destroy() {
				// Clean up the event listener when the element is removed.
				input.removeEventListener("input", validate);
			},
		};
	}

	export function maskMemberId(input, inputType, mask, value) {
		function maskValue(value) {
			let masked = "";
			const i = makeDigiter(value);
			if (!i.more) {
				return masked;
			}

			for (const m of mask) {
				if (m === "@" && i.nextAlpha()) {
					// consume first alpha
					masked += i.value;
				} else if (m === "9" && i.nextDigit()) {
					// consume digit
					masked += i.value;
				} else if (i.more && m !== "@" && m !== "9") {
					// use mask literal (e.g., '-', ' ')
					masked += m;
				} else if (!i.more) {
					// ran out of characters from input
					break;
				}
			}
			return masked;
		}

		function makeDigiter(value) {
			const str = String(value ?? "");

			// Enforce: first character must be alphabetic (A�Z / a�z)
			// If the first char is not alphabetic, we emit nothing yet.
			const alpha = /^[A-Za-z]/.test(str) ? str[0] : "";

			// If you want to restrict to only 'M' or 'm', replace the above with:
			// const alpha = /^[Mm]/.test(str) ? str[0] : "";

			const digits = alpha ? str.slice(1).replace(/\D/g, "") : "";

			let pos = 0;
			const totalLen = (alpha ? 1 : 0) + digits.length;

			return {
				more: totalLen > 0,
				value: null,

				// Consume the alpha (only once, at the beginning)
				nextAlpha() {
					if (!alpha || pos !== 0) return false;
					this.value = alpha;
					pos += 1;
					this.more = pos < totalLen;
					return true;
				},

				// Consume the next digit
				nextDigit() {
					const digitIndex = pos - (alpha ? 1 : 0);
					if (digitIndex < 0 || digitIndex >= digits.length) return false;
					this.value = digits[digitIndex];
					pos += 1;
					this.more = pos < totalLen;
					return true;
				},
			};
		}

		async function queueRestoreSelection(input, start, end) {
			await tick();
			input.setSelectionRange(start, end);
		}

		function isRightOfMask(position) {
			// Right of a literal means the char to the left in the mask is NOT a placeholder
			// Treat '@' (alpha placeholder) and '9' (digit placeholder) as non-literals.
			return position > 1 && !["9", "@"].includes(mask[position - 1]);
		}

		function fixIt(input, state, sanitized) {
			if (state === sanitized && sanitized !== input.value) {
				// Because 'SvelteDOMSetProperty' isn't fired
				input.value = sanitized;
			}
			return sanitized;
		}

		const {
			selectionStart: oldStart,
			selectionEnd: oldEnd,
			value: { length: oldLength },
		} = input;
		let [start, end] = [oldStart, oldEnd];

		value = fixIt(input, value, maskValue(input.value));

		const { length } = value;
		switch (inputType) {
			case "insertText":
				{
					const adjust = length - oldLength;
					if (adjust > 0) {
						start += adjust;
					} else if (isRightOfMask(start)) {
						start += 1;
					}
				}
				break;

			case "deleteContentBackward":
				if (isRightOfMask(start)) {
					// Immediately to the right of non-placeholder literal,
					// move left over the literal so deletion hits the nearest digit/alpha.
					start -= 1;
				}
				break;

			case "deleteContentForward":
				if (start === length && isRightOfMask(start)) {
					// End of content, immediately to the right of non-placeholder literal:
					// move left over the literal so deletion hits the nearest digit.
					start -= 1;
				}
				break;
		}

		if (oldStart === oldEnd) {
			end = start;
		}
		// queueRestoreSelection(input, start, end);
		return value;
	}

	export function maskA1CValue(input, inputType, mask, value) {
  function getDigits(v) {
    return String(v ?? "").replace(/\D/g, "").slice(0, 3); // only up to 3 digits
  }

  function formatA1C(digits) {
    if (digits.length === 0) return "";

    if (digits.length === 1) {
      // fractional-only: .d%
      return `.${digits}%`;
    }

    if (digits.length === 2) {
      // 1 integer + 1 fraction: d.f%
      return `${digits[0]}.${digits[1]}%`;
    }

    // 3 digits: prefer 2-int + 1-frac (dd.d%), with range enforcement
    const int2 = parseInt(digits.slice(0, 2), 10);
    const frac = digits[2];

    if (int2 > 70) {
      // fallback to 1-int rule (ignore 3rd digit): d.f%
      return `${digits[0]}.${digits[1]}%`;
    }
    if (int2 === 70) {
      // only allow 70.0%
      return frac === "0" ? `70.0%` : `70%`;
    }
    // normal: dd.d%
    return `${digits.slice(0, 2)}.${frac}%`;
  }

  function maskValue(v) {
    const digits = getDigits(v);
    return formatA1C(digits);
  }

  // ---- Caret helpers ----
  async function queueRestoreSelection(el, start, end) {
    // Fallback if Svelte's tick() is not available
    const nextTick = typeof tick === "function" ? tick : () => Promise.resolve();
    await nextTick();
    try { el.setSelectionRange(start, end); } catch {}
  }

  function isLiteralAt(formatted, pos) {
    // pos is caret position in the formatted string
    // Literal characters in our A1C mask are '.' and '%'
    return pos > 0 && /[.%]/.test(formatted[pos - 1]);
  }

  // ---- Main flow ----
  const {
    selectionStart: oldStart,
    selectionEnd: oldEnd,
    value: { length: oldLength },
  } = input;

  let [start, end] = [oldStart, oldEnd];

  // Sanitize current displayed value
  const sanitized = maskValue(input.value);

  // Apply if changed
  if (sanitized !== input.value) {
    input.value = sanitized;
  }

  const newLength = sanitized.length;

  // Adjust caret based on the type of input event
  switch (inputType) {
    case "insertText": {
      const adjust = newLength - oldLength;
      if (adjust > 0) {
        // Net growth -> advance caret by the growth
        start += adjust;
      } else if (isLiteralAt(sanitized, start)) {
        // If just to the right of a literal ('.' or '%'), nudge past it
        start = Math.min(start + 1, newLength);
      }
      break;
    }

    case "deleteContentBackward": {
      // If just to the right of a literal, move left one so deletion hits a digit
      if (isLiteralAt(sanitized, start)) {
        start = Math.max(0, start - 1);
      }
      break;
    }

    case "deleteContentForward": {
      // If at the end and just to the right of a literal, move left to delete a digit
      if (start === newLength && isLiteralAt(sanitized, start)) {
        start = Math.max(0, start - 1);
      }
      break;
    }
  }

  // Collapse selection if it was a caret
  if (oldStart === oldEnd) {
    end = start;
  }

  queueRestoreSelection(input, start, end);
  return sanitized;
}


</script>
